import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";

import { Icon } from "../../ui";
import Pagination from "../../ui/Data/Pagination";
import { useBusinessesForProducts } from "../../../hooks/useProducts";

const ProductsIndex = () => {
  const { t } = useTranslation(["products", "common"]);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when search term actually changes (after debounce)
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const { data, isLoading, isError, refetch } = useBusinessesForProducts({
    page,
    pageSize: 20,
    searchTerm,
  });

  const businesses = data?.businesses || [];
  const pagination = data?.pagination || {};

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />

      {/* Search Filter */}
      <div className="border-border bg-background rounded-lg border p-4">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          {/* Search Input */}
          <div className="w-full min-w-0 flex-1 sm:w-auto">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="text-muted-foreground h-5 w-5" />
              </div>
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchInput}
                onChange={handleSearchChange}
                className="border-border bg-card focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  onClick={handleClearSearch}
                  className="bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full transition-colors"
                  type="button"
                >
                  <svg
                    className="text-foreground h-3 w-3 hover:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Button>
              )}
            </div>
          </div>

          {/* Refresh Button */}
          <div className="w-full flex-shrink-0 sm:w-auto">
            <Button
              variant="default"
              onClick={handleRefresh}
              disabled={isLoading}
              className="focus:ring-ring inline-flex h-10 w-full items-center justify-center border border-transparent px-3 py-2 leading-4 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <svg
                className={`mr-1 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {isLoading ? t("actions.refreshing") : t("actions.refresh")}
            </Button>
          </div>
        </div>
      </div>

      <div className="">
        {isError ? (
          <div
            role="alert"
            className="border-border rounded-lg border p-6 text-center"
          >
            <p className="text-destructive">{t("common:states.error")}</p>
            <Button type="button" className="mt-4" onClick={() => refetch()}>
              {t("common:actions.retry")}
            </Button>
          </div>
        ) : isLoading ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="border-border bg-background animate-pulse rounded-lg border p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-card h-12 w-12 rounded-lg" />
                    <div className="min-w-0 flex-1">
                      <div className="bg-card mb-2 h-4 w-40 rounded" />
                      <div className="bg-card h-3 w-56 rounded" />
                    </div>
                  </div>
                  <div className="bg-card mt-3 h-4 w-24 rounded" />
                </div>
              ))}
            </div>

            <div className="mt-4">
              <div className="border-border bg-card animate-pulse rounded-lg border p-4">
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="bg-secondary h-4 w-64 rounded" />
                  <div className="bg-secondary h-8 w-48 rounded" />
                </div>
              </div>
            </div>
          </>
        ) : businesses.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
            {businesses.map((b) => (
              <Button
                variant="ghost"
                key={b.slug || b.id}
                onClick={() => navigate(`/menu/products/${b.id}`)}
                className="group border-border bg-background hover:border-border hover:bg-card h-full rounded-lg border p-4 text-left transition-colors"
              >
                <div className="flex min-w-0 items-start gap-3">
                  {b.logo ? (
                    <img
                      src={b.logo}
                      alt={b.name}
                      className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="bg-card flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
                      <Icon
                        name="Store"
                        className="text-muted-foreground h-6 w-6"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-white">
                      {b.name}
                    </div>
                    <div className="text-muted-foreground truncate text-sm">
                      {b.address || b.city?.name || ""}
                    </div>
                  </div>
                </div>
                <div className="text-muted-foreground mt-3 flex items-center text-xs">
                  <span className="mr-1">{t("actions.manage")}</span>
                  <Icon name="ChevronRight" className="h-4 w-4" />
                </div>
              </Button>
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground py-16 text-center">
            {t("empty.noBusinesses")}
          </div>
        )}

        {pagination?.total_pages > 1 && (
          <div className="mt-4">
            <div className="border-border bg-card rounded-lg border p-4">
              <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                <div className="text-muted-foreground text-sm">
                  {(() => {
                    const pageSizeLocal = pagination?.page_size || 20;
                    const total = pagination?.total || 0;
                    const start = total ? (page - 1) * pageSizeLocal + 1 : 0;
                    const end = Math.min(page * pageSizeLocal, total);
                    return (
                      <>
                        {t("pagination.showing", {
                          from: start,
                          to: end,
                          total,
                        })}
                      </>
                    );
                  })()}
                </div>

                <Pagination
                  currentPage={page}
                  totalPages={pagination.total_pages}
                  onPageChange={setPage}
                  maxVisiblePages={5}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsIndex;
